import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaCalendarAlt, FaClock, FaPlus, FaSpinner, FaTrash } from 'react-icons/fa';

const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

function formatHour(value) {
  if (!value) {
    return '--:--';
  }
  return value.slice(0, 5);
}

function Horarios() {
  const [horarios, setHorarios] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [newHorario, setNewHorario] = useState({
    dia_inicio: 1,
    dia_fin: 5,
    hora_inicio: '09:00',
    hora_fin: '18:00',
  });

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }
    return { Authorization: `Bearer ${token}` };
  };

  async function fetchHorarios() {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/horarios', { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      const sorted = [...data].sort((a, b) => {
        if (a.dia_semana !== b.dia_semana) {
          return a.dia_semana - b.dia_semana;
        }
        return String(a.hora_inicio).localeCompare(String(b.hora_inicio));
      });
      setHorarios(sorted);
    } catch (err) {
      setError(`Error al cargar horarios: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchHorarios();
  }, []);

  function handleInputChange(e) {
    const { name, value } = e.target;
    setNewHorario((prev) => ({ ...prev, [name]: value }));
  }

  function validateHorario() {
    const diaInicio = Number(newHorario.dia_inicio);
    const diaFin = Number(newHorario.dia_fin);

    if (diaInicio < 1 || diaInicio > 7 || diaFin < 1 || diaFin > 7 || diaInicio > diaFin) {
      return 'Selecciona un rango de días válido (1 a 7 y día inicio <= día fin).';
    }
    if (!newHorario.hora_inicio || !newHorario.hora_fin) {
      return 'Debes indicar hora de inicio y fin.';
    }
    if (newHorario.hora_inicio >= newHorario.hora_fin) {
      return 'La hora de inicio debe ser menor que la hora de fin.';
    }
    return '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateHorario();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/horarios', newHorario, { headers: getAuthHeaders() });
      await fetchHorarios();
      setSuccess('Horario guardado correctamente.');
    } catch (err) {
      setError(`Error al crear horario: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar este horario?')) {
      return;
    }
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/api/horarios/${id}`, { headers: getAuthHeaders() });
      await fetchHorarios();
      setSuccess('Horario eliminado correctamente.');
    } catch (err) {
      setError(`Error al eliminar horario: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  }

  const grouped = useMemo(() => {
    const map = new Map();
    DIAS_SEMANA.forEach((d) => map.set(d.value, []));
    horarios.forEach((h) => {
      const day = Number(h.dia_semana);
      if (!map.has(day)) {
        map.set(day, []);
      }
      map.get(day).push(h);
    });
    return map;
  }, [horarios]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <h1 className="text-3xl font-bold text-slate-900">Horarios de atención</h1>
        <p className="mt-1 text-sm text-slate-500">
          Define tus franjas de disponibilidad para agendado automático.
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      ) : null}

      <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Nueva franja</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-slate-500">Día inicio</label>
            <select
              name="dia_inicio"
              value={newHorario.dia_inicio}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            >
              {DIAS_SEMANA.map((dia) => (
                <option key={dia.value} value={dia.value}>
                  {dia.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-slate-500">Día fin</label>
            <select
              name="dia_fin"
              value={newHorario.dia_fin}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            >
              {DIAS_SEMANA.map((dia) => (
                <option key={dia.value} value={dia.value}>
                  {dia.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-slate-500">Hora inicio</label>
            <input
              type="time"
              name="hora_inicio"
              value={newHorario.hora_inicio}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-slate-500">Hora fin</label>
            <input
              type="time"
              name="hora_fin"
              value={newHorario.hora_fin}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <FaSpinner className="mr-2 animate-spin" /> : <FaPlus className="mr-2" />}
              Agregar
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="flex h-52 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
          <FaSpinner className="animate-spin text-3xl text-slate-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {DIAS_SEMANA.map((dia) => {
            const dayItems = grouped.get(dia.value) || [];
            return (
              <div key={dia.value} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-900">
                    <FaCalendarAlt className="mr-2 inline text-slate-400" />
                    {dia.label}
                  </h3>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                    {dayItems.length} franja{dayItems.length === 1 ? '' : 's'}
                  </span>
                </div>

                {dayItems.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin disponibilidad configurada.</p>
                ) : (
                  <div className="space-y-2">
                    {dayItems.map((horario) => (
                      <div key={horario.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-sm font-medium text-slate-800">
                          <FaClock className="mr-1 inline text-slate-400" />
                          {formatHour(horario.hora_inicio)} - {formatHour(horario.hora_fin)}
                        </p>
                        <button
                          onClick={() => handleDelete(horario.id)}
                          disabled={deletingId === horario.id}
                          className="inline-flex items-center rounded-lg bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deletingId === horario.id ? <FaSpinner className="mr-1 animate-spin" /> : <FaTrash className="mr-1" />}
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Horarios;
